import React, { useState } from 'react';
import { getOrders } from '../../../lib/api';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Download, FileText, FileSpreadsheet, FileJson, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const EXPORT_FORMATS = [
  {
    id: 'csv',
    label: 'CSV',
    sublabel: 'Für Excel / Google Sheets',
    icon: FileSpreadsheet,
    color: 'bg-green-50 text-green-700 border-green-200',
    ext: 'csv',
    mime: 'text/csv',
  },
  {
    id: 'json',
    label: 'JSON',
    sublabel: 'Für Buchhaltungssoftware',
    icon: FileJson,
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    ext: 'json',
    mime: 'application/json',
  },
  {
    id: 'dsfinvk',
    label: 'DSFinV-K',
    sublabel: 'Finanzamt / Steuerprüfung (DE)',
    icon: FileText,
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    ext: 'csv',
    mime: 'text/csv',
  },
];

function ordersToCSV(orders) {
  const header = [
    'Bestell-Nr','Datum','Uhrzeit','Tisch','Status','Zahlungsart',
    'Subtotal','Rabatt','MwSt-Gesamt','Gesamt','Kellner'
  ].join(';');

  const rows = orders.map(o => [
    o.order_number || o.id?.slice(-6) || '',
    o.created_at ? new Date(o.created_at).toLocaleDateString('de-AT') : '',
    o.created_at ? new Date(o.created_at).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' }) : '',
    o.table_name || o.table_id || '',
    o.status || '',
    o.payment_method || '',
    (o.subtotal || 0).toFixed(2).replace('.', ','),
    (o.discount_amount || 0).toFixed(2).replace('.', ','),
    (o.vat_total || 0).toFixed(2).replace('.', ','),
    (o.total || 0).toFixed(2).replace('.', ','),
    o.cashier_name || '',
  ].join(';'));

  return [header, ...rows].join('\n');
}

function ordersToDSFinVK(orders) {
  // Simplified DSFinV-K Einzelaufzeichnungsmodul (Z_EINZELAUFZEICHNUNG)
  const header = [
    'Z_KASSE_ID','Z_ERSTELLUNG','Z_NR','BON_ID','BON_NR','BON_TYP',
    'BON_NAME','DATUM','UHRZEIT','TERMINAL_ID','AGENTUR_ID',
    'POS_TERMINAL_ID','KASSE_UST_ZUORDNUNG','BON_STORNO',
    'BON_START','BON_ENDE','UMATZ_BRUTTO','STEUERSATZ','STEUER_SIGNAL',
    'ZAHLART_BETRAG','ZAHLART_WAEHRUNG','ZAHLART'
  ].join(';');

  const rows = orders.map((o, idx) => {
    const created = o.created_at ? new Date(o.created_at) : new Date();
    return [
      'KASSE001',
      new Date().toISOString(),
      idx + 1,
      o.id || '',
      o.order_number || '',
      'Beleg',
      o.order_number || '',
      created.toLocaleDateString('de-AT'),
      created.toLocaleTimeString('de-AT'),
      'POS_001',
      '',
      'POS_001',
      'AT',
      o.status === 'cancelled' ? '1' : '0',
      o.created_at || '',
      o.completed_at || o.created_at || '',
      (o.total || 0).toFixed(2).replace('.', ','),
      '10,00',
      'C',
      (o.total || 0).toFixed(2).replace('.', ','),
      'EUR',
      o.payment_method === 'cash' ? 'Bar' : 'Karte',
    ].join(';');
  });

  return [header, ...rows].join('\n');
}

function downloadBlob(content, filename, mime) {
  const BOM = '\uFEFF'; // UTF-8 BOM for Excel compatibility
  const blob = new Blob([BOM + content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminReportsExports() {
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [format, setFormat] = useState('csv');
  const [isExporting, setIsExporting] = useState(false);
  const [lastExport, setLastExport] = useState(null);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Fetch completed orders (and cancelled for compliance)
      const [completed, cancelled] = await Promise.all([
        getOrders('completed', null),
        getOrders('cancelled', null),
      ]);

      let orders = [...completed, ...cancelled].filter(o => {
        if (!o.created_at) return false;
        const d = o.created_at.slice(0, 10);
        return d >= dateFrom && d <= dateTo;
      });

      orders.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      if (orders.length === 0) {
        toast.error('Keine Bestellungen im gewählten Zeitraum');
        return;
      }

      let content, filename, mime;
      const period = `${dateFrom}_bis_${dateTo}`;

      if (format === 'csv') {
        content = ordersToCSV(orders);
        filename = `FiscalPOS_Export_${period}.csv`;
        mime = 'text/csv';
      } else if (format === 'json') {
        content = JSON.stringify(orders, null, 2);
        filename = `FiscalPOS_Export_${period}.json`;
        mime = 'application/json';
      } else if (format === 'dsfinvk') {
        content = ordersToDSFinVK(orders);
        filename = `FiscalPOS_DSFinVK_${period}.csv`;
        mime = 'text/csv';
      }

      downloadBlob(content, filename, mime);
      setLastExport({ count: orders.length, filename, ts: new Date() });
      toast.success(`${orders.length} Bestellungen exportiert`);
    } catch {
      toast.error('Fehler beim Export');
    } finally {
      setIsExporting(false);
    }
  };

  const fmtObj = EXPORT_FORMATS.find(f => f.id === format);

  return (
    <div className="p-6 space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-[#1a2f38]">Exporte</h1>
        <p className="text-slate-500">CSV, JSON und DSFinV-K Export für Buchhaltung und Finanzamt</p>
      </div>

      {/* Format selection */}
      <div>
        <Label className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 block">Format</Label>
        <div className="grid grid-cols-3 gap-3">
          {EXPORT_FORMATS.map((f) => {
            const Icon = f.icon;
            return (
              <button
                key={f.id}
                onClick={() => setFormat(f.id)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  format === f.id
                    ? 'border-[#1a2f38] bg-white shadow-md'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg border flex items-center justify-center mb-3 ${f.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className="font-semibold text-slate-800">{f.label}</p>
                <p className="text-xs text-slate-400 mt-0.5">{f.sublabel}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Date range */}
      <Card className="shadow-sm">
        <CardContent className="p-5 space-y-4">
          <Label className="text-sm font-semibold text-slate-500 uppercase tracking-wider block">Zeitraum</Label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Von</Label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Bis</Label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} min={dateFrom} />
            </div>
          </div>

          {/* Quick ranges */}
          <div className="flex gap-2 flex-wrap">
            {[
              ['Heute', () => {
                const t = new Date().toISOString().slice(0, 10);
                setDateFrom(t); setDateTo(t);
              }],
              ['Diese Woche', () => {
                const now = new Date();
                const mon = new Date(now); mon.setDate(now.getDate() - now.getDay() + 1);
                setDateFrom(mon.toISOString().slice(0, 10));
                setDateTo(now.toISOString().slice(0, 10));
              }],
              ['Dieser Monat', () => {
                const now = new Date();
                setDateFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
                setDateTo(now.toISOString().slice(0, 10));
              }],
              ['Letzter Monat', () => {
                const now = new Date();
                const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                const last  = new Date(now.getFullYear(), now.getMonth(), 0);
                setDateFrom(first.toISOString().slice(0, 10));
                setDateTo(last.toISOString().slice(0, 10));
              }],
            ].map(([label, fn]) => (
              <button
                key={label}
                onClick={fn}
                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Export button */}
      <Button
        onClick={handleExport}
        disabled={isExporting}
        className="w-full h-14 text-base bg-[#1a2f38] hover:bg-[#2a4048]"
      >
        {isExporting ? (
          <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Exportiere…</>
        ) : (
          <><Download className="w-5 h-5 mr-2" /> {fmtObj?.label} exportieren</>
        )}
      </Button>

      {/* Last export info */}
      {lastExport && (
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="text-sm text-emerald-800">
            <p className="font-medium">Export erfolgreich</p>
            <p className="text-emerald-600 mt-0.5">
              {lastExport.count} Bestellungen · {lastExport.filename}
            </p>
          </div>
        </div>
      )}

      {/* Compliance note */}
      <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4">
        <AlertCircle className="w-5 h-5 text-slate-400 mt-0.5 shrink-0" />
        <div className="text-sm text-slate-600">
          <p className="font-medium text-slate-700">GoBD / RKSV Hinweis</p>
          <p className="mt-0.5 text-slate-500">
            Alle Transaktionsdaten müssen 10 Jahre aufbewahrt werden. DSFinV-K ist das von der deutschen Finanzbehörde vorgeschriebene Format für Kassensysteme (KassenSichV). Für österreichische Betriebe verwenden Sie den CSV-Export.
          </p>
        </div>
      </div>
    </div>
  );
}
